import { Button } from 'components';
import { TEXT_STRING } from '../../../constants';
import { memo, useCallback, useMemo } from 'react';
import {
  EChannelType,
  EUserProvider,
  TMember,
  TMaleDetail,
  TRecommendFemale,
} from 'types';
import { useOrder } from 'hooks';
import { useUserStore } from 'store';

type Props = {
  channelType?: EChannelType;
  channelId?: string;
  users: TMember[];
};
const ChatOrder = ({ channelType, channelId, users }: Props) => {
  // Hooks
  const { gotoCall } = useOrder();
  const { user } = useUserStore();

  // Memo
  const isPrivateChannel = useMemo(
    () => channelType === EChannelType.PRIVATE,
    [channelType],
  );

  const isSalonChannel = useMemo(
    () => channelType === EChannelType.SALON,
    [channelType],
  );

  const female = useMemo(
    () =>
      users.find(
        (female) => female.provider === EUserProvider.FEMALE_APPLICATION,
      ),
    [users],
  );

  const males = useMemo(
    () =>
      users.filter(
        (member) => member.provider === EUserProvider.MALE_APPLICATION,
      ),
    [users],
  );

  const handleCallByName = useCallback(() => {
    if (!female) return;

    const nominatedFemale: TRecommendFemale = {
      id: female.id,
      nickname: female.name,
      avatar: female.avatar || '',
      isPeriodTime: female.isPeriodTime || false,
      isMeeting: female.isFemaleMeeting || false,
      nominationFee: female.nominationFee || 0,
    };

    gotoCall({
      nominatedFemale,
      fixedFemaleId: female.id,
      sourceChannelId: channelId,
    });
  }, [gotoCall, female, channelId]);

  const handleCreateGroupWithMales = useCallback(() => {
    if (males.length === 0) return;
    const invitedMales: TMaleDetail[] = males.map((male) => ({
      id: male.id,
      nickname: male.name,
      avatar: male.avatar || '',
      biography: male.biography || '',
      age: 0,
      income: 0,
      invoice: 0,
      order: 0,
      collection: [],
      ranking: male.ranking || 0,
      currentPoint: 0,
      area: '',
      isSalonMember: !!male.isSalonMember,
      isSalonPremiumMember: !!male.isSalonPremiumMember,
      salonMembershipType: male.salonMembershipType ?? null,
    }));
    gotoCall({ invitedMales, sourceChannelId: channelId });
  }, [gotoCall, males, channelId]);

  const shouldShowCallButton =
    isPrivateChannel && !!female && users.every(({ inactive }) => !inactive);

  const shouldShowGroupButton =
    isSalonChannel &&
    !!user?.isSalonMember &&
    males.length > 0 &&
    males.every((male) => !!male.isSalonMember) &&
    users.every(({ inactive }) => !inactive);

  if (!shouldShowCallButton && !shouldShowGroupButton) {
    return null;
  }

  const groupButtonLabel =
    males.length > 1
      ? TEXT_STRING.LIST_MALE.CREATE_PROJECT
      : TEXT_STRING.MESSAGE.CREATE_GROUP_WITH_THIS_PERSON;

  return (
    <Button
      onClick={
        shouldShowGroupButton ? handleCreateGroupWithMales : handleCallByName
      }
      className="text-xs whitespace-nowrap p-8px"
    >
      {shouldShowGroupButton
        ? groupButtonLabel
        : TEXT_STRING.CALL_WOMAN.PLAY_WITH_KID}
    </Button>
  );
};

export default memo(ChatOrder);
